import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { BrandLogoComponent } from '../brand-logo/brand-logo.component';

export type BrandedLoaderMode = 'page' | 'compact' | 'inline';

@Component({
  selector: 'app-branded-loader',
  standalone: true,
  imports: [BrandLogoComponent],
  template: `
    <div class="branded-loader"
      [class.branded-loader--compact]="mode() === 'compact'"
      [class.branded-loader--inline]="mode() === 'inline'"
      role="status" aria-live="polite" aria-busy="true">
      <span class="logo-stage" aria-hidden="true">
        <span class="logo-halo"></span>
        <app-brand-logo class="loader-logo" />
      </span>
      @if (label()) {
        <span class="loader-label">{{ label() }}</span>
      }
      @if (mode() !== 'inline') {
        <span class="progress-track" aria-hidden="true">
          <span></span><span></span><span></span><span></span>
        </span>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .branded-loader {
      min-height: 18rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      color: var(--accent-logo);
    }

    .logo-stage {
      position: relative;
      display: grid;
      width: 6rem;
      height: 6rem;
      place-items: center;
    }

    .loader-logo {
      position: relative;
      z-index: 1;
      width: 5rem;
      height: 5rem;
      color: currentColor;
      animation: brand-stride 1.65s ease-in-out infinite;
      filter: drop-shadow(0 10px 18px color-mix(in srgb, var(--region-accent) 22%, transparent));
    }

    .logo-halo {
      position: absolute;
      inset: 0;
      border-radius: 999px;
      border: 1px solid color-mix(in srgb, var(--accent-logo) 26%, transparent);
      background: color-mix(in srgb, var(--accent-logo) 8%, transparent);
      animation: brand-halo 1.65s ease-out infinite;
    }

    .loader-label {
      max-width: 22rem;
      color: color-mix(in srgb, currentColor 72%, #64748b);
      font-size: .75rem;
      font-weight: 900;
      letter-spacing: .14em;
      line-height: 1.4;
      text-align: center;
      text-transform: uppercase;
    }

    .progress-track {
      display: flex;
      height: .35rem;
      align-items: flex-end;
      gap: .22rem;
    }

    .progress-track span {
      width: .35rem;
      border-radius: 999px;
      background: currentColor;
      animation: brand-bars 1.15s ease-in-out infinite;
    }

    .progress-track span:nth-child(1) { height: .35rem; animation-delay: -.45s; }
    .progress-track span:nth-child(2) { height: .55rem; animation-delay: -.3s; }
    .progress-track span:nth-child(3) { height: .75rem; animation-delay: -.15s; }
    .progress-track span:nth-child(4) { height: 1rem; }

    .branded-loader--compact {
      min-height: 10rem;
      gap: .75rem;
    }

    .branded-loader--compact .logo-stage {
      width: 4.75rem;
      height: 4.75rem;
    }

    .branded-loader--compact .loader-logo {
      width: 4rem;
      height: 4rem;
    }

    .branded-loader--inline {
      min-height: 0;
      flex-direction: row;
      gap: .55rem;
      color: inherit;
    }

    .branded-loader--inline .logo-stage {
      width: 1.6rem;
      height: 1.6rem;
    }

    .branded-loader--inline .loader-logo {
      width: 1.45rem;
      height: 1.45rem;
      filter: none;
    }

    .branded-loader--inline .logo-halo { display: none; }

    .branded-loader--inline .loader-label {
      color: inherit;
      font-size: inherit;
      letter-spacing: inherit;
      line-height: inherit;
    }

    @keyframes brand-stride {
      0%, 100% { transform: translateY(0) scale(1); }
      50% { transform: translateY(-.22rem) scale(1.025); }
    }

    @keyframes brand-halo {
      0% { opacity: .75; transform: scale(.78); }
      70%, 100% { opacity: 0; transform: scale(1.12); }
    }

    @keyframes brand-bars {
      0%, 100% { opacity: .28; transform: scaleY(.55); }
      50% { opacity: 1; transform: scaleY(1); }
    }

    @media (prefers-reduced-motion: reduce) {
      .loader-logo, .logo-halo, .progress-track span { animation: none; }
      .logo-halo { opacity: .18; }
      .progress-track span { opacity: .72; }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandedLoaderComponent {
  readonly label = input('Chargement…');
  readonly mode = input<BrandedLoaderMode>('page');
}
